import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster } from 'sonner';
import { ArrowLeft, CircleNotch, Flame, Snowflake, ArrowsClockwise, Target } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const verdictStyle = {
  OVERDUE: 'bg-rose-100 text-rose-800 ring-2 ring-rose-400',
  DUE: 'bg-amber-100 text-amber-800 ring-2 ring-amber-400',
  EARLY: 'bg-sky-50 text-sky-800 ring-1 ring-sky-300',
  NO_PRECEDENT: 'bg-gray-100 text-gray-500',
  IN_RANGE: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
};

const stateStyle = {
  HOT: 'bg-orange-100 text-orange-800 ring-2 ring-orange-400',
  COLD: 'bg-blue-100 text-blue-800 ring-2 ring-blue-400',
  NEUTRAL: 'bg-gray-50 text-gray-500',
};

const devColor = (dev) => {
  if (dev > 5) return 'text-rose-700 font-bold';
  if (dev < -5) return 'text-blue-700 font-bold';
  if (dev > 2) return 'text-amber-600';
  if (dev < -2) return 'text-sky-600';
  return 'text-gray-500';
};

export default function Window63Reversion() {
  return <ReversionContent embedded={false} />;
}

export function ReversionContent({ embedded = false, initialTab = 'summary' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab);
  const [windowSize, setWindowSize] = useState(30);

  const fetchData = useCallback(async (w) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/reversion-analyzer`, { params: { window: w } });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(windowSize); }, [windowSize, fetchData]);

  useEffect(() => {
    const h = () => fetchData(windowSize);
    window.addEventListener('euromillions:draws-updated', h);
    return () => window.removeEventListener('euromillions:draws-updated', h);
  }, [windowSize, fetchData]);

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'}>
      {!embedded && <Toaster position="top-right" />}
      {!embedded && (
      <header className="bg-white border-b">
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
            <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><ArrowsClockwise size={24} weight="fill" className="text-amber-400" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Window 63 — Reversion Analyzer</h1>
              <p className="text-xs text-gray-500">Hot/Cold streak timing for tiers and numbers · when mean-reversion kicks in</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-semibold">Window:</label>
            {[15, 30, 50].map((w) => (
              <button key={w}
                onClick={() => setWindowSize(w)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${windowSize === w ? 'bg-[#002FA7] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid={`window-${w}`}>
                {w} draws
              </button>
            ))}
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 border-t">
          {[
            ['summary', 'Live Signals', '(3 blocks)'],
            ['tiers', 'Tier Reversion', '(1 block)'],
            ['numbers', 'Per-Number Reversion', '(1 block)'],
          ].map(([id, label, blocks]) => (
            <button key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-3 text-sm font-bold transition-colors border-b-2 ${tab === id ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              data-testid={`tab-${id}`}>
              {label} <span className="text-[10px] font-normal opacity-70">{blocks}</span>
            </button>
          ))}
        </div>
      </header>
      )}
      {embedded && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <label className="text-xs text-gray-500 font-semibold">Reversion Window:</label>
          {[15, 30, 50].map((w) => (
            <button key={w}
              onClick={() => setWindowSize(w)}
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${windowSize === w ? 'bg-[#002FA7] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              data-testid={`window-${w}-embed`}>
              {w} draws
            </button>
          ))}
          <div className="flex gap-1 ml-3">
            {[
              ['summary', 'Live Signals'],
              ['tiers', 'Tier Reversion'],
              ['numbers', 'Per-Number'],
            ].map(([id, label]) => (
              <button key={id}
                onClick={() => setTab(id)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${tab === id ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
                data-testid={`subtab-${id}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <main className={embedded ? '' : 'max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-6'}>
        {loading ? (
          <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data?.error ? (
          <div className="bg-rose-50 border border-rose-200 rounded p-6 text-center text-rose-700 font-bold">{data.error}</div>
        ) : data ? (
          <>
            {tab === 'summary' && (
              <>
                {/* Block 1 — Tier Reversions Ready */}
                <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-tier-ready">
                  <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2"><Target size={18} weight="bold" /> Block 1 — Tier Reversions Ready ({data.summary?.top_tier_reversions?.length || 0})</h2>
                  <p className="text-xs text-gray-500 mb-3">Ball×Tier combos where the current streak has reached or passed historical median reversion point.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800 text-white">
                        <tr>
                          <th className="px-3 py-2 text-left">Ball · Tier</th>
                          <th className="px-3 py-2 text-center">Direction</th>
                          <th className="px-3 py-2 text-center">Dev %</th>
                          <th className="px-3 py-2 text-center">Streak</th>
                          <th className="px-3 py-2 text-center">Median rev.</th>
                          <th className="px-3 py-2 text-center">P25–P75</th>
                          <th className="px-3 py-2 text-center">Verdict</th>
                          <th className="px-3 py-2 text-left">What to expect</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.summary?.top_tier_reversions || []).map((r, i) => (
                          <tr key={i} className="border-b hover:bg-gray-50" data-testid={`sum-tier-${i}`}>
                            <td className="px-3 py-2 font-mono font-bold">{r.ball} · <span className={r.tier === 'H' ? 'text-green-700' : r.tier === 'M' ? 'text-amber-700' : 'text-red-700'}>{r.tier}</span></td>
                            <td className="px-3 py-2 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${stateStyle[r.direction] || 'bg-gray-100'}`}>
                                {r.direction === 'HOT' ? <Flame size={12} weight="fill" /> : r.direction === 'COLD' ? <Snowflake size={12} weight="fill" /> : null}
                                {r.direction}
                              </span>
                            </td>
                            <td className={`px-3 py-2 text-center font-mono font-bold ${devColor(r.current_dev)}`}>{r.current_dev > 0 ? '+' : ''}{r.current_dev}%</td>
                            <td className="px-3 py-2 text-center font-mono font-bold text-gray-900">{r.current_streak}</td>
                            <td className="px-3 py-2 text-center font-mono">{r.median_reversion ?? '—'}</td>
                            <td className="px-3 py-2 text-center font-mono text-gray-500">{r.p25_reversion ?? '—'}–{r.p75_reversion ?? '—'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase ${verdictStyle[r.verdict] || 'bg-gray-100'}`}>{r.verdict}</span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-700">{r.verdict_text}</td>
                          </tr>
                        ))}
                        {(data.summary?.top_tier_reversions || []).length === 0 && (
                          <tr><td colSpan="8" className="px-3 py-6 text-center text-gray-400 italic">No tier reversions currently OVERDUE or DUE</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Block 2 — Cold Numbers Ready */}
                <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-cold-ready">
                  <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2"><Snowflake size={18} weight="fill" className="text-blue-500" /> Block 2 — Cold Numbers Ready to Rebound</h2>
                  <p className="text-xs text-gray-500 mb-3">Overdue numbers where 2+ affinity triggers have fired in the last 3 draws.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-blue-900 text-white">
                        <tr>
                          <th className="px-3 py-2 text-left">Number</th>
                          <th className="px-3 py-2 text-center">Gap</th>
                          <th className="px-3 py-2 text-center">Avg Gap</th>
                          <th className="px-3 py-2 text-center">Overdue×</th>
                          <th className="px-3 py-2 text-center">Triggers</th>
                          <th className="px-3 py-2 text-left">Top Affinities (lift)</th>
                          <th className="px-3 py-2 text-left">Forecast</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.summary?.cold_ready_numbers || []).map((r, i) => (
                          <tr key={r.number} className="border-b hover:bg-gray-50" data-testid={`sum-cold-${r.number}`}>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-900 font-mono font-black text-lg ring-2 ring-blue-300">{r.number}</span>
                            </td>
                            <td className="px-3 py-2 text-center font-mono font-bold text-rose-700">{r.current_gap}</td>
                            <td className="px-3 py-2 text-center font-mono text-gray-500">{r.avg_gap}</td>
                            <td className="px-3 py-2 text-center font-mono font-bold">{r.overdue_score}×</td>
                            <td className="px-3 py-2 text-center">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-bold">🔥 {r.n_triggers_fired}/5 fired</span>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex gap-1.5">
                                {(r.affinities || []).map(a => (
                                  <span key={a.number} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px] font-mono" title={`lift ${a.lift}×, ${a.pair_count} pairs`}>
                                    {a.number}<span className="text-gray-400 text-[9px]">×{a.lift}</span>
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-[11px] text-gray-700 italic">{r.forecast}</td>
                          </tr>
                        ))}
                        {(data.summary?.cold_ready_numbers || []).length === 0 && (
                          <tr><td colSpan="7" className="px-3 py-6 text-center text-gray-400 italic">No cold numbers with 2+ fired triggers right now</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Block 3 — Hot Numbers Cooldown */}
                <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-hot-cooldown">
                  <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2"><Flame size={18} weight="fill" className="text-orange-500" /> Block 3 — Hot Numbers Cooling Down Soon</h2>
                  <p className="text-xs text-gray-500 mb-3">Numbers currently over-indexing in the window by ≥1.5× vs long-run rate. Watch for mean reversion.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-orange-900 text-white">
                        <tr>
                          <th className="px-3 py-2 text-left">Number</th>
                          <th className="px-3 py-2 text-center">Lift</th>
                          <th className="px-3 py-2 text-center">Recent %</th>
                          <th className="px-3 py-2 text-center">Overall %</th>
                          <th className="px-3 py-2 text-center">Gap</th>
                          <th className="px-3 py-2 text-center">Max Gap</th>
                          <th className="px-3 py-2 text-left">Forecast</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.summary?.hot_cooldown_numbers || []).map((r, i) => (
                          <tr key={r.number} className="border-b hover:bg-gray-50" data-testid={`sum-hot-${r.number}`}>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 text-orange-900 font-mono font-black text-lg ring-2 ring-orange-300">{r.number}</span>
                            </td>
                            <td className="px-3 py-2 text-center font-mono font-bold text-rose-700">{r.lift}×</td>
                            <td className="px-3 py-2 text-center font-mono">{r.recent_rate_pct}%</td>
                            <td className="px-3 py-2 text-center font-mono text-gray-500">{r.overall_rate_pct}%</td>
                            <td className="px-3 py-2 text-center font-mono">{r.current_gap}</td>
                            <td className="px-3 py-2 text-center font-mono text-gray-500">{r.max_gap}</td>
                            <td className="px-3 py-2 text-[11px] text-gray-700 italic">{r.forecast}</td>
                          </tr>
                        ))}
                        {(data.summary?.hot_cooldown_numbers || []).length === 0 && (
                          <tr><td colSpan="7" className="px-3 py-6 text-center text-gray-400 italic">No hot numbers right now</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}

            {tab === 'tiers' && (
              <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-tier-full">
                <h2 className="font-bold text-gray-900 mb-1">Block 1 — Full Tier Reversion Table (5 balls × 3 tiers = 15 rows)</h2>
                <p className="text-xs text-gray-500 mb-4">For each ball position and tier, current deviation vs long-run, historical precedents and expected reversion timing.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Ball</th>
                        <th className="px-3 py-2 text-center">Tier</th>
                        <th className="px-3 py-2 text-center">Direction</th>
                        <th className="px-3 py-2 text-center">Baseline</th>
                        <th className="px-3 py-2 text-center">Current</th>
                        <th className="px-3 py-2 text-center">Dev</th>
                        <th className="px-3 py-2 text-center">Streak</th>
                        <th className="px-3 py-2 text-center">Precedents</th>
                        <th className="px-3 py-2 text-center">Median</th>
                        <th className="px-3 py-2 text-center">P25</th>
                        <th className="px-3 py-2 text-center">P75</th>
                        <th className="px-3 py-2 text-center">Max</th>
                        <th className="px-3 py-2 text-center">Verdict</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.tier_reversion || []).map((r, i) => (
                        <tr key={i} className={`border-b hover:bg-gray-50 ${r.verdict === 'OVERDUE' ? 'bg-rose-50' : r.verdict === 'DUE' ? 'bg-amber-50' : ''}`} data-testid={`tier-${r.ball_key}-${r.tier}`}>
                          <td className="px-3 py-2 font-mono font-bold">{r.ball}</td>
                          <td className="px-3 py-2 text-center font-mono font-bold">{r.tier}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${stateStyle[r.direction] || 'bg-gray-100'}`}>
                              {r.direction === 'HOT' ? <Flame size={12} weight="fill" /> : r.direction === 'COLD' ? <Snowflake size={12} weight="fill" /> : null}
                              {r.direction}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center font-mono text-gray-500">{r.baseline_pct}%</td>
                          <td className="px-3 py-2 text-center font-mono">{r.current_pct}%</td>
                          <td className={`px-3 py-2 text-center font-mono ${devColor(r.current_dev)}`}>{r.current_dev > 0 ? '+' : ''}{r.current_dev}</td>
                          <td className="px-3 py-2 text-center font-mono font-bold">{r.current_streak}</td>
                          <td className="px-3 py-2 text-center font-mono text-gray-500">{r.n_precedents}</td>
                          <td className="px-3 py-2 text-center font-mono font-bold">{r.median_reversion ?? '—'}</td>
                          <td className="px-3 py-2 text-center font-mono text-gray-500">{r.p25_reversion ?? '—'}</td>
                          <td className="px-3 py-2 text-center font-mono text-gray-500">{r.p75_reversion ?? '—'}</td>
                          <td className="px-3 py-2 text-center font-mono text-gray-500">{r.max_reversion ?? '—'}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase ${verdictStyle[r.verdict] || 'bg-gray-100'}`}>{r.verdict}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {tab === 'numbers' && (
              <section className="bg-white border rounded-lg p-5 mb-6" data-testid="block-numbers-full">
                <h2 className="font-bold text-gray-900 mb-1">Block 1 — Per-Number Reversion (All 50 Numbers)</h2>
                <p className="text-xs text-gray-500 mb-4">State, gap analysis, top-5 affinities and trigger firing status for every number 1–50.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-800 text-white">
                      <tr>
                        <th className="px-2 py-2 text-left">#</th>
                        <th className="px-2 py-2 text-center">State</th>
                        <th className="px-2 py-2 text-center">Recent%</th>
                        <th className="px-2 py-2 text-center">Overall%</th>
                        <th className="px-2 py-2 text-center">Lift</th>
                        <th className="px-2 py-2 text-center">Gap</th>
                        <th className="px-2 py-2 text-center">Avg</th>
                        <th className="px-2 py-2 text-center">Max</th>
                        <th className="px-2 py-2 text-center">Overdue×</th>
                        <th className="px-2 py-2 text-center">Triggers</th>
                        <th className="px-2 py-2 text-left">Top Affinities</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.number_reversion || []).map((r) => (
                        <tr key={r.number} className={`border-b hover:bg-gray-50 ${r.state === 'HOT' ? 'bg-orange-50/40' : r.state === 'COLD' ? 'bg-blue-50/40' : ''}`} data-testid={`num-${r.number}`}>
                          <td className="px-2 py-1.5 font-mono font-bold text-base">{r.number}</td>
                          <td className="px-2 py-1.5 text-center">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${stateStyle[r.state]}`}>
                              {r.state === 'HOT' ? <Flame size={10} weight="fill" /> : r.state === 'COLD' ? <Snowflake size={10} weight="fill" /> : null}
                              {r.state}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-center font-mono">{r.recent_rate_pct}%</td>
                          <td className="px-2 py-1.5 text-center font-mono text-gray-500">{r.overall_rate_pct}%</td>
                          <td className={`px-2 py-1.5 text-center font-mono font-bold ${r.lift >= 1.5 ? 'text-orange-700' : r.lift <= 0.5 ? 'text-blue-700' : 'text-gray-500'}`}>{r.lift}×</td>
                          <td className="px-2 py-1.5 text-center font-mono">{r.current_gap}</td>
                          <td className="px-2 py-1.5 text-center font-mono text-gray-500">{r.avg_gap}</td>
                          <td className="px-2 py-1.5 text-center font-mono text-gray-500">{r.max_gap}</td>
                          <td className={`px-2 py-1.5 text-center font-mono font-bold ${r.overdue_score >= 1.5 ? 'text-rose-700' : 'text-gray-500'}`}>{r.overdue_score}×</td>
                          <td className="px-2 py-1.5 text-center">{r.trigger_tag}</td>
                          <td className="px-2 py-1.5">
                            <div className="flex flex-wrap gap-1">
                              {(r.affinities || []).map(a => (
                                <span key={a.number} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[10px] font-mono" title={`lift ${a.lift}×, ${a.pair_count} pairs`}>
                                  {a.number}<span className="text-gray-400 text-[9px]">×{a.lift}</span>
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
